import React, { useEffect, useState } from 'react'
import './EventsDashboardNew.css'
import { Link } from 'react-router-dom'
import { IoIosAdd } from 'react-icons/io'
import Axios from 'axios'
import { IoStar } from 'react-icons/io5'
import ClipLoader from 'react-spinners/ClipLoader'
import { API_URL } from '../../config'

const url = `${API_URL}/apps/kscanner/webservices.php`

const toNum = (v) => parseFloat(String(v ?? '').replace(/\s/g, '')) || 0

function formatCurrency(amount) {
  const number = toNum(amount)
  return number.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const TIMING = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'ongoing', label: 'Ongoing' },
  { key: 'past', label: 'Past' },
]

const Overview = ({ counts, gross, net }) => {
  const total = TIMING.reduce((s, t) => s + (Number(counts[t.key]) || 0), 0)

  return (
    <section className="ed-overview" aria-label="Overview">
      <div className="ed-overview-events">
        <div className="ed-bar" role="img" aria-label="Events by timing">
          {total === 0 ? (
            <span className="ed-seg ed-seg-empty" style={{ width: '100%' }} />
          ) : (
            TIMING.map((t) => {
              const pct = ((Number(counts[t.key]) || 0) / total) * 100
              return pct > 0 ? (
                <span
                  key={t.key}
                  className={`ed-seg ed-seg-${t.key}`}
                  style={{ width: `${pct}%` }}
                />
              ) : null
            })
          )}
        </div>
        <dl className="ed-stats">
          {TIMING.map((t) => (
            <div key={t.key} className="ed-stat">
              <dt>
                <i className={`ed-dot ed-seg-${t.key}`} />
                {t.label}
              </dt>
              <dd>{counts[t.key] ?? 0}</dd>
            </div>
          ))}
        </dl>
      </div>

      <dl className="ed-overview-money">
        <div className="ed-stat">
          <dt>Gross</dt>
          <dd>R{formatCurrency(gross)}</dd>
        </div>
        <div className="ed-stat">
          <dt>Net</dt>
          <dd>R{formatCurrency(net)}</dd>
        </div>
      </dl>
    </section>
  )
}

const EventRow = ({ event }) => {
  const sales = toNum(event.sales)
  const net = toNum(event.net)
  const netRevenue = sales - net

  const organiser = event.organiser || event.businessname || ''
  const isDraft = event.budget <= 0
  const status = isDraft ? 'draft' : event.eventstatus.toLowerCase()
  const payout = event.payoutstatus.toLowerCase()
  const running = event.campaignstatus === 'running'

  return (
    <tr>
      <td data-label="Organiser" className="ed-clip" title={organiser}>
        {organiser}
      </td>
      <td data-label="Event" className="ed-event" title={event.eventname}>
        <span className="ed-clip-inner">
          {event.is_sponsorship == 1 && <IoStar className="ed-star" aria-label="Sponsored" />}
          {event.eventname}
        </span>
      </td>
      <td data-label="Date">{event.eventday}</td>
      <td data-label="Status">
        <span className={`ed-pill ed-pill-${status}`}>{isDraft ? 'draft' : event.eventstatus}</span>
      </td>
      <td data-label="Complete" className="ed-num">
        {event.sold}
      </td>
      <td data-label="Admit" className="ed-num">
        {event.admissions}
      </td>
      <td data-label="Gross" className="ed-num ed-amount">
        R{formatCurrency(sales)}
      </td>
      <td data-label="Net" className="ed-num ed-amount">
        R{formatCurrency(netRevenue)}
      </td>
      <td data-label="Payout">
        <span className={`ed-pill ed-pill-${payout}`}>{event.payoutstatus}</span>
      </td>
      <td data-label="Visibility">
        <span className={`ed-pill ${running ? 'ed-pill-active' : 'ed-pill-inactive'}`}>
          {running ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td data-label="" className="ed-action-cell">
        <Link
          className="ed-btn ed-btn-quiet"
          to={{
            pathname: './editevent',
            state: {
              is_sponsorship: event.is_sponsorship,
              campaignstatus: event.campaignstatus,
              eventid: event.eventid,
              eventname: event.eventname,
              poster: event.poster,
              eventdate: event.eventday,
              enddate: event.enddate,
              event_date: event.event_day,
              finish_date: event.finish_day,
              theme: event.theme,
              venue: event.venue,
              expectednum: event.expected,
              time: event.time,
              endtime: event.endtime,
              prevpage: 'myevents',
            },
          }}
        >
          View
        </Link>
      </td>
    </tr>
  )
}

const EventsDashboard = () => {
  const [ongoingEvents, setOngoingEvents] = useState(0)
  const [upcomingEvents, setUpcomingEvents] = useState(0)
  const [pastEvents, setPastEvents] = useState(0)
  const [eventList, setEventList] = useState([])
  const [revenue, setRevenue] = useState(0)
  const [netRevenue, setNetRevenue] = useState(0)
  const [loader, setLoader] = useState(true)

  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  const fetchongoingEventsData = async () => {
    Axios.get(`${url}?query=FETCH-EVENT-COUNTS`, { withCredentials: true })
      .then((response) => {
        setOngoingEvents(response.data.ongoing)
        setUpcomingEvents(response.data.upcoming)
        setPastEvents(response.data.past)
        setRevenue(response.data.revenue)
        setNetRevenue(response.data.netRevenue)
      })
      .catch((error) => {
        console.error('Error fetching dashboard data:', error)
      })
  }

  const fetchEvents = async () => {
    Axios.get(`${url}?query=EVENT-LIST`, { withCredentials: true })
      .then((response) => {
        setLoader(false)
        if (response.data.status === 'success') {
          setEventList(response.data.events)
        } else {
          setEventList([])
        }
      })
      .catch((error) => {
        setLoader(false)
        setEventList([])
        console.error('Error fetching dashboard data:', error)
      })
  }

  useEffect(() => {
    fetchongoingEventsData()
    fetchEvents()
  }, [])

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentEvents = eventList.slice(indexOfFirstItem, indexOfLastItem)
  const totalPages = Math.max(1, Math.ceil(eventList.length / itemsPerPage))
  const accountId = localStorage.getItem('businessid')

  return (
    <div className="events-dashboard ed-page">
      <header className="ed-head">
        <div>
          <h1 className="ed-title">Dashboard</h1>
          <p className="ed-sub">
            {eventList.length} {eventList.length === 1 ? 'event' : 'events'}
          </p>
        </div>

        <div className="ed-head-actions">
          {accountId == 120 && (
            <Link
              className="ed-btn ed-btn-quiet"
              to={{ pathname: 'index-events', state: { prevpage: 'dashboard' } }}
            >
              <IoIosAdd size={20} /> Index events
            </Link>
          )}
          <Link
            className="ed-btn ed-btn-primary"
            to={{ pathname: 'addevent', state: { prevpage: 'dashboard' } }}
          >
            <IoIosAdd size={20} /> Create event
          </Link>
        </div>
      </header>

      <Overview
        counts={{ upcoming: upcomingEvents, ongoing: ongoingEvents, past: pastEvents }}
        gross={revenue}
        net={netRevenue}
      />

      <div className="ed-table-wrap">
        <table className="ed-table">
          <thead>
            <tr>
              <th>Organiser</th>
              <th>Event</th>
              <th>Date</th>
              <th>Status</th>
              <th className="ed-num">Complete</th>
              <th className="ed-num">Admit</th>
              <th className="ed-num">Gross</th>
              <th className="ed-num">Net</th>
              <th>Payout</th>
              <th>Visibility</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loader ? (
              <tr className="ed-state-row">
                <td colSpan={11}>
                  <ClipLoader color="grey" loading size={20} />
                </td>
              </tr>
            ) : currentEvents.length === 0 ? (
              <tr className="ed-state-row">
                <td colSpan={11}>
                  <p>No events yet.</p>
                  <Link
                    className="ed-btn ed-btn-primary"
                    to={{ pathname: 'addevent', state: { prevpage: 'dashboard' } }}
                  >
                    <IoIosAdd size={20} /> Create your first event
                  </Link>
                </td>
              </tr>
            ) : (
              currentEvents.map((event) => <EventRow key={event.eventid} event={event} />)
            )}
          </tbody>
        </table>

        {eventList.length > itemsPerPage && (
          <nav className="ed-pager" aria-label="Pagination">
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </nav>
        )}
      </div>
    </div>
  )
}

export default EventsDashboard
